import "./markerList.scss";

import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withCharacterContext } from "../../components/character/CharacterContext";
import EmptyState from "../../components/emptyState";
import FormButton from "../../components/form/FormButton";
import FormEditableLabel from "../../components/form/FormEditableLabel";
import SplitButton from "../../components/form/SplitButton";
import ColorDot from "../../components/interface/ColorDot";
import Icon from "../../components/interface/Icon";
import ListItemView from "../../components/listView/ListItemView";
import ListView from "../../components/listView/ListView";
import PubSubTopics from "../../constants/pubSubTopics";
import { appStateOperations } from "../../modules/appState";
import { calendarMarkerOperations, calendarMarkerSelectors } from "../../modules/calendarMarkers";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import { uiStateSelectors } from "../../modules/uiState";
import { PropTypeShapes } from "../../types";
import { calcTextSize } from "../../utils/calcTextSize";
import { mergeDispatchToProps, wrapEditingComponent } from "../../utils/codeSplit";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import { trackEvent } from "../../utils/trackAnalytics";

const getInitialEditingStatus = (isSelected, label) => {
  let result = false;
  //#!if iOS !== true
  result = isSelected && (!label || label === getLabelWithUntitled());
  //#!endif
  return result;
};

const getMiddleElement = (label, onLabelChange, isSelected, getTextSizeFunction) => {
  const displayLabel = getLabelWithUntitled(label);
  return (
    <FormEditableLabel
      value={label}
      displayValue={displayLabel}
      placeholder={`Calendar Marker Label`}
      multiline={true}
      allowNewLine={false}
      onChange={onLabelChange}
      getSizeFunction={getTextSizeFunction}
      isEditing={getInitialEditingStatus(isSelected, label)}
    />
  );
};

const getRightElement = date => {
  return isDateValid(date) ? <Icon icon="sidebarScrollToDate" color="iconMono" tooltip="Scroll to marker" /> : null;
};

const scrollToMarker = date => {
  PubSub.publish(PubSubTopics.scrollToDate, date);
};

class CalendarMarkerList extends PureComponent {
  constructor(props) {
    super(props);

    this.markerRenderer = this.markerRenderer.bind(this);
    this.getTextSizeFunction = this.getTextSizeFunction.bind(this);
  }

  markerRenderer(marker, isSelected, ref) {
    const leftIcon = marker.icon ? <Icon icon={marker.icon} color={marker.color} /> : <ColorDot color={marker.color} />;
    const rightIcon = getRightElement(marker.startDate);
    return (
      <div ref={ref} key={marker.id}>
        <ListItemView
          key={marker.id}
          leftElements={leftIcon}
          middleElements={getMiddleElement(
            marker.label,
            label => this.props.onMarkerLabelChange(marker.id, label),
            isSelected,
            this.getTextSizeFunction
          )}
          rightElements={rightIcon}
          isSelected={isSelected}
          onClick={this.props.onMarkerClick}
          onRightElementsClick={() => scrollToMarker(marker.startDate)}
          clickContext={marker.id}
        />
      </div>
    );
  }

  getTextSizeFunction(text, maxWidth) {
    return calcTextSize(
      text,
      this.props.characterContext.getFontSize(),
      this.props.characterContext.getWidthMap(),
      maxWidth
    ).size;
  }

  getListView() {
    const { markers, selectedMarkerIds, onMarkerClick } = this.props;
    const selectedIndex = markers.findIndex(i => selectedMarkerIds.includes(i.id));
    const onItemClick = newIndex => onMarkerClick(markers.find((item, index) => index === newIndex).id);

    return (
      <ListView
        items={markers}
        selectedItemIndex={selectedIndex}
        onItemSelect={onItemClick}
        itemRenderer={this.markerRenderer}
        isEnterToSelect={false}
      />
    );
  }

  render() {
    const { markers, onCreateClick } = this.props;

    return (
      <div>
        <div className="markerList">
          <div className="markerListHeader">
            <Icon icon="pin" color="iconMono" />
            <span>Calendar Markers</span>
            {wrapEditingComponent(<SplitButton label="Add" onClick={onCreateClick} small={true} icon="sidebarAdd" />)}
          </div>
          {markers.size === 0 ? (
            <EmptyState svg="calendar" type="sidePanel">
              <div key="content">
                <p>Looks like you haven&apos;t added any Calendar Markers yet.</p>
                <p>Use Calendar Markers to highlight important dates and milestones on your timeline.</p>
                <FormButton primary={true} label={`Add New Calendar Marker`} onClick={onCreateClick} />
              </div>
            </EmptyState>
          ) : (
            this.getListView()
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    markers: calendarMarkerSelectors.getCalendarMarkersList(state.present),
    selectedMarkerIds: uiStateSelectors.getSelectedMarkers(state.present)
  };
};

const readonlyActions = {
  onMarkerClick: appStateOperations.selectMarker
};

const editingActions = {
  onCreateClick: () => {
    trackEvent("createCalendarMarkerFromLeftPanel", "createCalendarMarker");
    return calendarMarkerOperations.createMarker();
  },
  onMarkerLabelChange: (id, label) => {
    return calendarMarkerOperations.editLabel([id], label);
  }
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

CalendarMarkerList.propTypes = {
  onMarkerClick: PropTypes.func.isRequired,
  onCreateClick: PropTypes.func.isRequired,
  onMarkerLabelChange: PropTypes.func.isRequired,
  markers: PropTypeShapes.calendarMarkerList.isRequired,
  selectedMarkerIds: ImmutablePropTypes.listOf(PropTypes.string),
  // From character context
  characterContext: PropTypes.object.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(withCharacterContext(CalendarMarkerList));
